import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { gql, useMutation } from '@apollo/client';
import { useLogger, useSelector } from '~Hooks';
import { useIncomingAppOrders } from '~Providers/IncomingAppOrdersProvider';
import { SetPickupTime, SetPickupTimeVariables } from './__queries__';

const ModificationButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  height: 42px;
  width: 52px;
  color: #afb9c9;
  border: 2px solid #4a4e55;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  margin-right: 12px;
  &:active {
    background-color: #4a4e55;
  }
`;

const TimeContainer = styled.div`
  display: flex;
  flex-basis: 120px;
  flex-direction: column;
  align-items: center;
  padding: 0 24px;
  margin-left: -12px; // Removes margin space from button 2
  color: #afb9c9;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  & > h3 {
    font-size: 22px;
    margin: 0;
    white-space: nowrap;
  }
  & > span {
    justify-self: center;
    display: block;
  }
`;

const SET_PICKUP_TIME_MUTATION = gql`
  mutation SetPickupTime($venueId: String!, $timeInMinutes: Int!) {
    setMinimumPickupWaitTime(venueId: $venueId, timeInMinutes: $timeInMinutes) {
      success
    }
  }
`;

/**
 * Allows our users to control how long any subsequent orders will take to prepare.
 */
export default function PrepTimeControls() {
  const logger = useLogger('prep-time-controls');
  const venueId = useSelector((state) => state.venueId);
  const incomingAppOrders = useIncomingAppOrders();

  const [setPickupTime, { loading }] = useMutation<SetPickupTime, SetPickupTimeVariables>(
    SET_PICKUP_TIME_MUTATION,
  );

  useEffect(() => {
    // Refetch the prep time when the component mounts in case it has been changed by another device
    incomingAppOrders.prepTime.refetch();
  }, []);

  // On input, update the storage with new time
  const setCurrentPrepTime = async (newTime: number) => {
    if (!venueId) {
      logger.error('No venueId found');
      return;
    }

    try {
      const pickupTimeRes = await setPickupTime({
        variables: {
          venueId,
          timeInMinutes: newTime,
        },
      });

      incomingAppOrders.prepTime.refetch();

      if (pickupTimeRes.data?.setMinimumPickupWaitTime.success) {
        logger.info('Prep time updated successfully', {
          newTime,
          oldTime: incomingAppOrders.prepTime,
        });
      }
    } catch (error) {
      logger.error('Error setting pickup time', { errorData: { error } });
      return;
    }

    incomingAppOrders.prepTime.refetch();
  };

  if (incomingAppOrders.prepTime.loading) {
    return null;
  }

  const prepTime = incomingAppOrders.prepTime.time;

  return (
    <>
      <ModificationButton onTouchEnd={() => setCurrentPrepTime(prepTime > 5 ? prepTime - 5 : 1)}>
        -5
      </ModificationButton>
      <ModificationButton
        onTouchEnd={() => setCurrentPrepTime(prepTime > 1 ? prepTime - 1 : prepTime)}>
        -1
      </ModificationButton>
      <TimeContainer>
        <h3>{prepTime} min</h3>
        <span>Prep time</span>
      </TimeContainer>
      <ModificationButton onTouchEnd={() => setCurrentPrepTime(prepTime + 1)}>
        +1
      </ModificationButton>
      <ModificationButton onTouchEnd={() => setCurrentPrepTime(prepTime + 5)}>
        +5
      </ModificationButton>
    </>
  );
}
